import React, { useEffect, useRef, useState } from "react";
import { makeStyles } from "tss-react/mui";
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  TextField,
  Tooltip,
  Typography
} from "@mui/material";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import RestartAltOutlinedIcon from "@mui/icons-material/RestartAltOutlined";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";

const SUGGESTION_KEYS = ["s1", "s2", "s3", "s4"];

const useStyles = makeStyles()(theme => ({
  content: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) 360px",
    gap: theme.spacing(2),
    paddingTop: `${theme.spacing(1)} !important`,
    [theme.breakpoints.down("md")]: {
      gridTemplateColumns: "minmax(0, 1fr)"
    }
  },
  editor: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    minWidth: 0
  },
  textArea: {
    "& textarea": {
      fontFamily: "monospace",
      fontSize: 13,
      lineHeight: 1.5
    }
  },
  hint: {
    fontSize: 12,
    color: theme.palette.text.secondary
  },
  assistant: {
    display: "flex",
    flexDirection: "column",
    height: 520,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    overflow: "hidden"
  },
  assistantHeader: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(1, 1.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
    fontWeight: 600,
    fontSize: 14
  },
  progress: {
    height: 2,
    flexShrink: 0
  },
  messages: {
    flex: 1,
    overflowY: "auto",
    padding: theme.spacing(1.5),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
    ...theme.scrollbarStyles
  },
  bubble: {
    maxWidth: "88%",
    padding: theme.spacing(1, 1.25),
    borderRadius: 10,
    fontSize: 13,
    lineHeight: 1.45,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word"
  },
  assistantBubble: {
    alignSelf: "flex-start",
    background: theme.palette.action.hover
  },
  userBubble: {
    alignSelf: "flex-end",
    background: theme.palette.primary.main,
    color: theme.palette.primary.contrastText
  },
  intro: {
    fontSize: 13,
    color: theme.palette.text.secondary
  },
  suggestions: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(0.75)
  },
  composer: {
    display: "flex",
    alignItems: "flex-end",
    gap: theme.spacing(1),
    padding: theme.spacing(1),
    borderTop: `1px solid ${theme.palette.divider}`
  }
}));

const ManualKnowledgeDialog = ({ open, onClose, onSaved, initialName = "", initialText = "", lockName = false }) => {
  const { classes, cx } = useStyles();
  const [name, setName] = useState(initialName);
  const [text, setText] = useState(initialText);
  const [goal, setGoal] = useState("");
  const [turns, setTurns] = useState([]);
  const [answer, setAnswer] = useState("");
  const [thinking, setThinking] = useState(false);
  const [saving, setSaving] = useState(false);
  const messagesRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setName(initialName);
    setText(initialText);
    setGoal("");
    setTurns([]);
    setAnswer("");
  }, [open, initialName, initialText]);

  useEffect(() => {
    const node = messagesRef.current;
    if (node) node.scrollTop = node.scrollHeight;
  }, [turns, thinking]);

  const started = turns.length > 0;

  const ask = async (topic, nextTurns) => {
    setThinking(true);
    try {
      const { data } = await api.post("/knowledge/assistant", { goal: topic, turns: nextTurns, draft: text });
      setTurns([...nextTurns, { role: "assistant", content: data.message }]);
      if (data.draft) setText(data.draft);
    } catch (err) {
      toastError(err);
      setTurns(nextTurns);
    } finally {
      setThinking(false);
    }
  };

  const start = topic => {
    const value = String(topic || "").trim();
    if (!value || thinking) return;
    setGoal(value);
    if (!lockName && !name.trim()) setName(value);
    const nextTurns = [{ role: "user", content: value }];
    setTurns(nextTurns);
    ask(value, nextTurns);
  };

  const send = () => {
    const value = answer.trim();
    if (!value || thinking) return;
    const nextTurns = [...turns, { role: "user", content: value }];
    setTurns(nextTurns);
    setAnswer("");
    ask(goal, nextTurns);
  };

  const restart = () => {
    setGoal("");
    setTurns([]);
    setAnswer("");
  };

  const handleKeyDown = event => {
    if (event.key !== "Enter" || event.shiftKey) return;
    event.preventDefault();
    if (started) send();
    else start(answer);
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/knowledge/manual", { name: name.trim(), text });
      onSaved(data);
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  const busy = saving || thinking;

  return (
    <Dialog open={open} onClose={() => !busy && onClose()} maxWidth="lg" fullWidth>
      <DialogTitle>{i18n.t(lockName ? "knowledge.manual.editTitle" : "knowledge.manual.title")}</DialogTitle>
      <DialogContent className={classes.content}>
        <div className={classes.editor}>
          <TextField
            label={i18n.t("knowledge.name")}
            placeholder={i18n.t("knowledge.manual.namePlaceholder")}
            value={name}
            onChange={e => setName(e.target.value)}
            disabled={lockName}
            fullWidth
            size="small"
          />
          <TextField
            label={i18n.t("knowledge.manual.textLabel")}
            placeholder={i18n.t("knowledge.manual.textPlaceholder")}
            value={text}
            onChange={e => setText(e.target.value)}
            className={classes.textArea}
            multiline
            rows={17}
            fullWidth
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <Typography className={classes.hint}>{i18n.t("knowledge.manual.hint")}</Typography>
        </div>

        <div className={classes.assistant}>
          <div className={classes.assistantHeader}>
            <AutoAwesomeOutlinedIcon fontSize="small" color="primary" />
            <span style={{ flex: 1 }}>{i18n.t("knowledge.manual.assistantTitle")}</span>
            {started && (
              <Tooltip title={i18n.t("knowledge.manual.restart")}>
                <span>
                  <IconButton size="small" onClick={restart} disabled={thinking}>
                    <RestartAltOutlinedIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </div>
          {thinking ? <LinearProgress className={classes.progress} /> : <div className={classes.progress} />}
          <div className={classes.messages} ref={messagesRef}>
            {!started && (
              <>
                <Typography className={classes.intro}>{i18n.t("knowledge.manual.assistantIntro")}</Typography>
                <div className={classes.suggestions}>
                  {SUGGESTION_KEYS.map(key => (
                    <Chip
                      key={key}
                      size="small"
                      variant="outlined"
                      label={i18n.t(`knowledge.manual.suggestions.${key}`)}
                      onClick={() => start(i18n.t(`knowledge.manual.suggestions.${key}`))}
                      disabled={thinking}
                    />
                  ))}
                </div>
              </>
            )}
            {turns.map((turn, index) => (
              <div
                key={index}
                className={cx(classes.bubble, turn.role === "assistant" ? classes.assistantBubble : classes.userBubble)}
              >
                {turn.content}
              </div>
            ))}
            {thinking && (
              <div className={cx(classes.bubble, classes.assistantBubble)}>{i18n.t("knowledge.manual.thinking")}</div>
            )}
          </div>
          <div className={classes.composer}>
            <TextField
              value={answer}
              onChange={e => setAnswer(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={i18n.t(started ? "knowledge.manual.answerPlaceholder" : "knowledge.manual.goalPlaceholder")}
              multiline
              maxRows={4}
              fullWidth
              size="small"
              disabled={thinking}
            />
            <Tooltip title={i18n.t(started ? "knowledge.manual.send" : "knowledge.manual.start")}>
              <span>
                <IconButton
                  color="primary"
                  onClick={() => (started ? send() : start(answer))}
                  disabled={thinking || !answer.trim()}
                >
                  <SendOutlinedIcon />
                </IconButton>
              </span>
            </Tooltip>
          </div>
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          {i18n.t("knowledge.cancel")}
        </Button>
        <Button variant="contained" onClick={save} disabled={busy || !name.trim() || !text.trim()}>
          {saving ? i18n.t("knowledge.manual.saving") : i18n.t("knowledge.manual.save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ManualKnowledgeDialog;
